import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logService } from '../lib/logService';
import type { WebhookEventType } from '../types/forms';

// =====================================================
// TYPES
// =====================================================

export interface Webhook {
  id: string;
  form_id: string;
  name: string;
  url: string;
  method: 'POST' | 'PUT';
  headers: Record<string, string>;
  field_mapping: string[];
  include_scores: boolean;
  is_active: boolean;
  retry_count: number;
  event_filter: WebhookEventType | null; // null = 'response.complete' (backward compat)
  created_at: string;
  updated_at: string;
}

export interface CreateWebhookInput {
  form_id: string;
  name: string;
  url: string;
  method?: 'POST' | 'PUT';
  headers?: Record<string, string>;
  field_mapping?: string[];
  include_scores?: boolean;
  is_active?: boolean;
  retry_count?: number;
  event_filter?: WebhookEventType;
}

export interface UpdateWebhookInput {
  name?: string;
  url?: string;
  method?: 'POST' | 'PUT';
  headers?: Record<string, string>;
  field_mapping?: string[];
  include_scores?: boolean;
  is_active?: boolean;
  retry_count?: number;
  event_filter?: WebhookEventType;
}

// =====================================================
// HOOK
// =====================================================

export function useFormWebhooks(formId: string | undefined) {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWebhooks = useCallback(async () => {
    if (!formId) {
      setWebhooks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Note: RLS policy returns headers to form owner.
      // Edge Function uses service_role for actual dispatch.
      const { data, error: fetchError } = await supabase
        .from('form_webhooks')
        .select('*')
        .eq('form_id', formId)
        .order('created_at', { ascending: true });

      if (fetchError) {
        logService.error('webhooks.fetch', fetchError.message, { supabase_code: fetchError.code, form_id: formId });
        setError('Erro ao carregar webhooks');
        return;
      }

      setWebhooks(data as Webhook[]);
    } catch (err) {
      logService.error('webhooks.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      setError('Erro inesperado ao carregar webhooks');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const createWebhook = useCallback(async (
    input: CreateWebhookInput
  ): Promise<{ webhook: Webhook | null; error: string | null }> => {
    try {
      const { data, error: insertError } = await supabase
        .from('form_webhooks')
        .insert(input)
        .select()
        .single();

      if (insertError) {
        logService.error('webhooks.create', insertError.message, { supabase_code: insertError.code, form_id: input.form_id });
        return { webhook: null, error: 'Erro ao criar webhook' };
      }

      const webhook = data as Webhook;
      setWebhooks(prev => [...prev, webhook]);
      return { webhook, error: null };
    } catch (err) {
      logService.error('webhooks.create', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: input.form_id });
      return { webhook: null, error: 'Erro inesperado ao criar webhook' };
    }
  }, []);

  const updateWebhook = useCallback(async (
    webhookId: string,
    updates: UpdateWebhookInput
  ): Promise<{ webhook: Webhook | null; error: string | null }> => {
    try {
      const { data, error: updateError } = await supabase
        .from('form_webhooks')
        .update(updates)
        .eq('id', webhookId)
        .select()
        .single();

      if (updateError) {
        logService.error('webhooks.update', updateError.message, { supabase_code: updateError.code, webhook_id: webhookId });
        return { webhook: null, error: 'Erro ao atualizar webhook' };
      }

      const webhook = data as Webhook;
      setWebhooks(prev => prev.map(w => w.id === webhookId ? webhook : w));
      return { webhook, error: null };
    } catch (err) {
      logService.error('webhooks.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, webhook_id: webhookId });
      return { webhook: null, error: 'Erro inesperado ao atualizar webhook' };
    }
  }, []);

  const deleteWebhook = useCallback(async (
    webhookId: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error: deleteError } = await supabase
        .from('form_webhooks')
        .delete()
        .eq('id', webhookId);

      if (deleteError) {
        logService.error('webhooks.delete', deleteError.message, { supabase_code: deleteError.code, webhook_id: webhookId });
        return { error: 'Erro ao excluir webhook' };
      }

      setWebhooks(prev => prev.filter(w => w.id !== webhookId));
      return { error: null };
    } catch (err) {
      logService.error('webhooks.delete', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, webhook_id: webhookId });
      return { error: 'Erro inesperado ao excluir webhook' };
    }
  }, []);

  const toggleWebhook = useCallback(async (
    webhookId: string,
    isActive: boolean
  ): Promise<{ error: string | null }> => {
    const result = await updateWebhook(webhookId, { is_active: isActive });
    return { error: result.error };
  }, [updateWebhook]);

  return {
    webhooks,
    loading,
    error,
    refetch: fetchWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    toggleWebhook,
  };
}
